import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Iconify,
    Button,
    ButtonVariant,
    ButtonSize,
    CenterModal,
    ModalSize,
} from '@repo/design-system/components/ui';
import { FormBuilder } from '@repo/design-system/components/forms';
import { onResetForm } from '@repo/design-system/components/forms/utils';
import { SwalAlert } from '@gyandiksha/shared-ui';
import { SocialMediaLinkFormConfig, buildSocialMediaLinkFormConfig } from './config/SocialMediaLink.config';
import './SocialMediaCard.scss';

const PLATFORM_OPTIONS = [
    { id: 'LinkedIn', label: 'LinkedIn', icon: 'logos:linkedin' },
    { id: 'Instagram', label: 'Instagram', icon: 'skill-icons:instagram' },
    { id: 'GitHub', label: 'GitHub', icon: 'ri:github-fill' },
    { id: 'Facebook', label: 'Facebook', icon: 'logos:facebook' },
];

export const getSocialPlatformDetails = (item) => {
    const platformName = item?.name || '';
    const rawUrl = item?.url || '';

    if (!platformName && !rawUrl) {
        return null;
    }

    const normalizedName = platformName.toLowerCase();

    const matchedPlatform = PLATFORM_OPTIONS.find((platform) =>
        normalizedName.includes(platform.id.toLowerCase())
    );

    return {
        platformName: platformName || 'Social Link',
        rawUrl,
        targetUrl: /^https?:\/\//i.test(rawUrl)
            ? rawUrl
            : rawUrl
                ? `https://${rawUrl}`
                : '#',
        icon: matchedPlatform?.icon || 'ri:global-line',
    };
};

const SocialMediaCard = ({
    socialNetwork,
    onAdd,
    onEdit,
    onDelete,
    onFetchById,
    isSaving,
    isDeleting,
    title = 'Social Media',
}) => {
    const [modalState, setModalState] = useState({
        isOpen: false,
        mode: 'add',
    });
    const [selectedPlatform, setSelectedPlatform] = useState('LinkedIn');
    const [formData, setFormData] = useState(() => buildSocialMediaLinkFormConfig());
    const [editingId, setEditingId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const formRef = useRef();

    // A platform can be linked only once — already-added ones are blocked in the Add modal
    const isPlatformAdded = (platformId) =>
        socialNetwork.some((item) => item?.name?.toLowerCase() === platformId.toLowerCase());
    const availablePlatforms = PLATFORM_OPTIONS.filter((platform) => !isPlatformAdded(platform.id));
    const isAllPlatformsAdded = availablePlatforms.length === 0;

    const closeModal = () => {
        if (isSaving) {
            return;
        }

        setModalState({ isOpen: false, mode: 'add' });
        setSelectedPlatform('LinkedIn');
        onResetForm(formData, setFormData, buildSocialMediaLinkFormConfig().initialState);
        setEditingId(null);
    };

    const handleOpenAdd = () => {
        if (isAllPlatformsAdded) {
            return;
        }
        setModalState({ isOpen: true, mode: 'add' });
        setSelectedPlatform(availablePlatforms[0].id);
        onResetForm(formData, setFormData, buildSocialMediaLinkFormConfig().initialState);
        setEditingId(null);
    };

    const handleOpenEdit = async (item) => {
        let latestItem = item;

        if (item?.id && onFetchById) {
            const fetchedItem = await onFetchById(item.id);
            latestItem = fetchedItem || item;
        }

        const details = getSocialPlatformDetails(latestItem);

        setModalState({ isOpen: true, mode: 'edit' });
        setSelectedPlatform(details?.platformName || 'LinkedIn');
        onResetForm(formData, setFormData, { profileUrl: details?.rawUrl || '' });
        setEditingId(item?.id ?? null);
    };

    const handleDeleteClick = async (item) => {
        const { warning } = SwalAlert();

        const isConfirmed = await warning(
            'Delete Social Media Link?',
            'Are you sure you want to delete this social media link?',
            true,
            'Yes, Delete',
            true,
            'Cancel'
        );

        if (!isConfirmed) {
            return;
        }

        setDeletingId(item?.id ?? null);
        await onDelete?.(item);
        setDeletingId(null);
    };

    const handleSave = async () => {
        const formData = formRef.current?.getValidatedFormData();
        if (!formData) {
            return;
        }

        const url = formData.profileUrl.trim();

        if (modalState.mode === 'add' && isPlatformAdded(selectedPlatform)) {
            ToastService.error({
                description: `${selectedPlatform} link is already added. Please edit the existing link instead.`,
            });
            return;
        }

        const newItem = modalState.mode === 'add'
            ? { name: selectedPlatform, url }
            : { id: editingId, name: selectedPlatform, url };

        const isSuccess = modalState.mode === 'add'
            ? await onAdd?.(newItem)
            : await onEdit?.(newItem);

        if (isSuccess) {
            closeModal();
        }
    };

    return (
        <>
            <div className="social-media-card">
                <div className="social-media-card-header">
                    <h5 className="social-media-card-title">{title}</h5>

                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.SM}
                        leftIcon="ri:add-line"
                        className="social-media-add-btn"
                        onClick={handleOpenAdd}
                        disabled={isDeleting || isAllPlatformsAdded}
                        ttLabel={isAllPlatformsAdded ? 'All social media links are already added' : undefined}
                        targetId="add-social-link"
                    >
                        Add
                    </Button>
                </div>

                <div className="social-media-card-body">
                    {socialNetwork.length === 0 ? (
                        <div className="social-media-empty-box">
                            <p className="social-media-empty-text">
                                You haven't added any social media links yet.
                            </p>
                        </div>
                    ) : (
                        <div className="social-media-list">
                            {socialNetwork.map((item, index) => {
                                const details = getSocialPlatformDetails(item);
                                const isItemDeleting = isDeleting && deletingId === item?.id;

                                return (
                                    <div
                                        key={item?.id ?? `${details.platformName}-${details.rawUrl}-${index}`}
                                        className="social-media-item"
                                    >
                                        <div className="social-media-item-left">
                                            <div className="social-media-icon-wrapper">
                                                <Iconify
                                                    icon={details.icon}
                                                    width={26}
                                                    height={26}
                                                />
                                            </div>

                                            <div className="social-media-info">
                                                <span className="social-media-platform-name">
                                                    {details.platformName}
                                                </span>

                                                <a
                                                    href={details.targetUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="social-media-url"
                                                    title={details.targetUrl}
                                                >
                                                    {details.rawUrl || details.targetUrl}
                                                </a>
                                            </div>
                                        </div>

                                        <div className="social-media-item-actions">
                                            <Button
                                                variant={ButtonVariant.GHOST}
                                                size={ButtonSize.MD}
                                                iconWidth={18}
                                                leftIcon="bytesize:edit"
                                                className="social-media-action-btn edit-btn"
                                                onClick={() => handleOpenEdit(item)}
                                                ttLabel="Edit link"
                                                targetId={`edit-social-link-${index}`}
                                                disabled={isDeleting}
                                            />

                                            <Button
                                                variant={ButtonVariant.GHOST}
                                                size={ButtonSize.MD}
                                                iconWidth={18}
                                                leftIcon="majesticons:delete-bin-line"
                                                className="social-media-action-btn delete-btn"
                                                onClick={() => handleDeleteClick(item)}
                                                ttLabel="Delete link"
                                                targetId={`delete-social-link-${index}`}
                                                loading={isItemDeleting}
                                                disabled={isDeleting && !isItemDeleting}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            <CenterModal
                isOpen={modalState.isOpen}
                handleToggleModal={closeModal}
                modalTitle={
                    modalState.mode === 'add'
                        ? 'Add Social Media Link'
                        : 'Edit Social Media Link'
                }
                modelSizeClass={ModalSize.WD50}
            >
                <div className="social-modal-body p-2">
                    <p
                        className="text-muted mb-3 fs-14"
                        style={{ color: '#64748b' }}
                    >
                        {modalState.mode === 'add'
                            ? 'Add your social media profile to complete your profile.'
                            : 'Update your social media profile details.'}
                    </p>

                    {modalState.mode === 'add' && (
                        <div className="form-group mb-4">
                            <label className="social-form-label">
                                Select Platform{' '}
                                <span className="required-star">*</span>
                            </label>

                            <div className="platform-grid">
                                {PLATFORM_OPTIONS.map((platform) => {
                                    const isSelected =
                                        selectedPlatform === platform.id;
                                    const isAdded = isPlatformAdded(platform.id);
                                    const isDisabled = isSaving || isAdded;

                                    return (
                                        <div
                                            key={platform.id}
                                            className={`platform-card ${
                                                isSelected ? 'selected' : ''
                                            } ${isDisabled ? 'disabled' : ''}`}
                                            onClick={() =>
                                                !isDisabled && setSelectedPlatform(platform.id)
                                            }
                                            aria-disabled={isDisabled}
                                            title={isAdded ? `${platform.label} link is already added` : undefined}
                                        >
                                            <div className="platform-card-left">
                                                <Iconify
                                                    icon={platform.icon}
                                                    width={24}
                                                    height={24}
                                                />
                                                <span className="platform-name">
                                                    {platform.label}
                                                </span>
                                            </div>

                                            {isAdded ? (
                                                <span className="platform-added-badge">
                                                    Added
                                                </span>
                                            ) : (
                                            <div
                                                className={`platform-radio ${
                                                    isSelected ? 'checked' : ''
                                                }`}
                                            >
                                                {isSelected && (
                                                    <div className="platform-radio-dot" />
                                                )}
                                            </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="form-group mb-4">
                        <FormBuilder ref={formRef} config={formData} />

                        <p className="url-hint-text">
                            {modalState.mode === 'add'
                                ? 'Example: https://www.linkedin.com/in/your-profile'
                                : 'Please make sure your profile URL is correct and publicly accessible.'}
                        </p>
                    </div>

                    <div className="d-flex justify-content-end gap-2 pt-3 border-top">
                        <Button
                            type="button"
                            variant={ButtonVariant.DANGER}
                            size={ButtonSize.MD}
                            className="px-4"
                            onClick={closeModal}
                            disabled={isSaving}
                        >
                            Cancel
                        </Button>

                        <Button
                            type="button"
                            variant={ButtonVariant.PRIMARY}
                            size={ButtonSize.MD}
                            className="px-4"
                            onClick={handleSave}
                            loading={isSaving}
                        >
                            {modalState.mode === 'add'
                                ? 'Add Link'
                                : 'Save Changes'}
                        </Button>
                    </div>
                </div>
            </CenterModal>
        </>
    );
};

SocialMediaCard.propTypes = {
    socialNetwork: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            url: PropTypes.string.isRequired,
        })
    ),
    onAdd: PropTypes.func,
    onEdit: PropTypes.func,
    onDelete: PropTypes.func,
    onFetchById: PropTypes.func,
    isSaving: PropTypes.bool,
    isDeleting: PropTypes.bool,
    title: PropTypes.string,
};

SocialMediaCard.defaultProps = {
    socialNetwork: [],
};

export default SocialMediaCard;
